/**
 * Tooth notation conversion utilities for FDI (ISO 3950) and Haderup systems.
 * 
 * FDI is the international standard used in Denmark.
 * Haderup was used in Clinio v1 for compatibility.
 */

export type ToothNotation = 'fdi' | 'haderup';
export type Quadrant = 'upper-right' | 'upper-left' | 'lower-left' | 'lower-right';

/**
 * FDI quadrant codes:
 * 1 = upper right (permanent)
 * 2 = upper left (permanent)
 * 3 = lower left (permanent)
 * 4 = lower right (permanent)
 * 5 = upper right (primary)
 * 6 = upper left (primary)
 * 7 = lower left (primary)
 * 8 = lower right (primary)
 */

const ARABIC_TO_ROMAN: Record<number, string> = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
  5: 'V',
};

const ROMAN_TO_ARABIC: Record<string, number> = {
  'I': 1,
  'II': 2,
  'III': 3,
  'IV': 4,
  'V': 5,
};

/**
 * Validate FDI tooth notation format.
 * Valid permanent: 11-18, 21-28, 31-38, 41-48
 * Valid primary: 51-55, 61-65, 71-75, 81-85
 */
export function isValidFdi(fdi: string): boolean {
  if (!/^\d{2}$/.test(fdi)) return false;
  
  const quadrant = parseInt(fdi[0]);
  const position = parseInt(fdi[1]);
  
  // Permanent teeth (quadrants 1-4)
  if (quadrant >= 1 && quadrant <= 4) {
    return position >= 1 && position <= 8;
  }
  
  // Primary teeth (quadrants 5-8)
  if (quadrant >= 5 && quadrant <= 8) {
    return position >= 1 && position <= 5;
  }
  
  return false;
}

/**
 * Validate Haderup tooth notation format.
 * Permanent: "1+" to "8+", "+1" to "+8", "-1" to "-8", "1-" to "8-"
 * Primary: "I+" to "V+", "+I" to "+V", "-I" to "-V", "I-" to "V-"
 */
export function isValidHaderup(haderup: string): boolean {
  // Permanent teeth patterns
  const permanentPatterns = [
    /^[1-8]\+$/,  // upper right: 1+ to 8+
    /^\+[1-8]$/,  // upper left: +1 to +8
    /^-[1-8]$/,   // lower left: -1 to -8
    /^[1-8]-$/,   // lower right: 1- to 8-
  ];
  
  // Primary teeth patterns
  const primaryPatterns = [
    /^(I|II|III|IV|V)\+$/,  // upper right: I+ to V+
    /^\+(I|II|III|IV|V)$/,  // upper left: +I to +V
    /^-(I|II|III|IV|V)$/,   // lower left: -I to -V
    /^(I|II|III|IV|V)-$/,   // lower right: I- to V-
  ];
  
  return [...permanentPatterns, ...primaryPatterns].some(pattern => pattern.test(haderup));
}

/**
 * Check if FDI code represents a primary (deciduous) tooth.
 * Primary teeth have quadrant codes 5-8.
 */
export function isPrimaryTooth(fdi: string): boolean {
  if (!isValidFdi(fdi)) return false;
  const quadrant = parseInt(fdi[0]);
  return quadrant >= 5 && quadrant <= 8;
}

/**
 * Get quadrant from FDI tooth code.
 */
export function getQuadrant(fdi: string): Quadrant {
  if (!isValidFdi(fdi)) {
    throw new Error(`Invalid FDI notation: ${fdi}`);
  }
  
  const quadrant = parseInt(fdi[0]);
  
  // Map both permanent and primary quadrants to anatomical position
  switch (quadrant) {
    case 1:
    case 5:
      return 'upper-right';
    case 2:
    case 6:
      return 'upper-left';
    case 3:
    case 7:
      return 'lower-left';
    case 4:
    case 8:
      return 'lower-right';
    default:
      throw new Error(`Invalid FDI notation: ${fdi}`);
  }
}

/**
 * Get tooth position (1-8 for permanent, 1-5 for primary) from FDI code.
 */
export function getToothPosition(fdi: string): number {
  if (!isValidFdi(fdi)) {
    throw new Error(`Invalid FDI notation: ${fdi}`);
  }
  return parseInt(fdi[1]);
}

/**
 * Convert FDI notation to Haderup notation.
 * 
 * Examples:
 * - "11" → "1+"  (upper right central incisor)
 * - "21" → "+1"  (upper left central incisor)
 * - "31" → "-1"  (lower left central incisor)
 * - "41" → "1-"  (lower right central incisor)
 * - "55" → "V+"  (upper right primary second molar)
 */
export function fdiToHaderup(fdi: string): string {
  if (!isValidFdi(fdi)) {
    throw new Error(`Invalid FDI notation: ${fdi}`);
  }
  
  const quadrant = parseInt(fdi[0]);
  const position = parseInt(fdi[1]);
  const isPrimary = quadrant >= 5;
  
  // Convert position to appropriate format (Arabic or Roman numeral)
  const positionStr = isPrimary ? ARABIC_TO_ROMAN[position] : position.toString();
  
  // Map quadrant to Haderup notation
  switch (quadrant) {
    case 1:
    case 5:
      return `${positionStr}+`;  // upper right
    case 2:
    case 6:
      return `+${positionStr}`;  // upper left
    case 3:
    case 7:
      return `-${positionStr}`;  // lower left
    case 4:
    case 8:
      return `${positionStr}-`;  // lower right
    default:
      throw new Error(`Invalid FDI notation: ${fdi}`);
  }
}

/**
 * Convert Haderup notation to FDI notation.
 * 
 * Examples:
 * - "1+" → "11"  (upper right central incisor)
 * - "+1" → "21"  (upper left central incisor)
 * - "-1" → "31"  (lower left central incisor)
 * - "1-" → "41"  (lower right central incisor)
 * - "V+" → "55"  (upper right primary second molar)
 */
export function haderupToFdi(haderup: string): string {
  if (!isValidHaderup(haderup)) {
    throw new Error(`Invalid Haderup notation: ${haderup}`);
  }
  
  // Determine if primary tooth by checking for Roman numerals
  const isPrimary = /[IVX]/.test(haderup);
  
  // Extract position and convert to number
  let position: number;
  let quadrantCode: number;
  
  if (haderup.endsWith('+')) {
    // Upper right
    const posStr = haderup.slice(0, -1);
    position = isPrimary ? ROMAN_TO_ARABIC[posStr] : parseInt(posStr);
    quadrantCode = isPrimary ? 5 : 1;
  } else if (haderup.startsWith('+')) {
    // Upper left
    const posStr = haderup.slice(1);
    position = isPrimary ? ROMAN_TO_ARABIC[posStr] : parseInt(posStr);
    quadrantCode = isPrimary ? 6 : 2;
  } else if (haderup.startsWith('-')) {
    // Lower left
    const posStr = haderup.slice(1);
    position = isPrimary ? ROMAN_TO_ARABIC[posStr] : parseInt(posStr);
    quadrantCode = isPrimary ? 7 : 3;
  } else if (haderup.endsWith('-')) {
    // Lower right
    const posStr = haderup.slice(0, -1);
    position = isPrimary ? ROMAN_TO_ARABIC[posStr] : parseInt(posStr);
    quadrantCode = isPrimary ? 8 : 4;
  } else {
    throw new Error(`Invalid Haderup notation: ${haderup}`);
  }
  
  return `${quadrantCode}${position}`;
}

/**
 * Get all teeth in a specific quadrant as FDI codes.
 */
export function getQuadrantTeeth(quadrant: Quadrant, includePrimary = false): string[] {
  const permanentQuadrantMap: Record<Quadrant, number> = {
    'upper-right': 1,
    'upper-left': 2,
    'lower-left': 3,
    'lower-right': 4,
  };
  
  const primaryQuadrantMap: Record<Quadrant, number> = {
    'upper-right': 5,
    'upper-left': 6,
    'lower-left': 7,
    'lower-right': 8,
  };
  
  const teeth: string[] = [];
  const quadrantCode = permanentQuadrantMap[quadrant];
  
  // Add permanent teeth (positions 1-8)
  for (let i = 1; i <= 8; i++) {
    teeth.push(`${quadrantCode}${i}`);
  }
  
  // Add primary teeth if requested (positions 1-5)
  if (includePrimary) {
    const primaryQuadrantCode = primaryQuadrantMap[quadrant];
    for (let i = 1; i <= 5; i++) {
      teeth.push(`${primaryQuadrantCode}${i}`);
    }
  }
  
  return teeth;
}

/**
 * Get all permanent teeth FDI codes (32 teeth).
 * Returns teeth ordered by quadrant: upper right, upper left, lower left, lower right.
 */
export function getAllPermanentTeeth(): string[] {
  const teeth: string[] = [];
  
  // Quadrants 1-4 (permanent), positions 1-8
  for (let quadrant = 1; quadrant <= 4; quadrant++) {
    for (let position = 1; position <= 8; position++) {
      teeth.push(`${quadrant}${position}`);
    }
  }
  
  return teeth;
}

/**
 * Get all primary (deciduous) teeth FDI codes (20 teeth).
 * Returns teeth ordered by quadrant: upper right, upper left, lower left, lower right.
 */
export function getAllPrimaryTeeth(): string[] {
  const teeth: string[] = [];
  
  // Quadrants 5-8 (primary), positions 1-5
  for (let quadrant = 5; quadrant <= 8; quadrant++) {
    for (let position = 1; position <= 5; position++) {
      teeth.push(`${quadrant}${position}`);
    }
  }
  
  return teeth;
}

/**
 * Format tooth for display based on user's preferred notation system.
 */
export function formatTooth(fdi: string, notation: ToothNotation): string {
  if (!isValidFdi(fdi)) {
    throw new Error(`Invalid FDI notation: ${fdi}`);
  }
  
  if (notation === 'fdi') {
    return fdi;
  }
  
  return fdiToHaderup(fdi);
}
